import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  Heading,
  ListItemAction,
  Popover,
  Tabs,
  Text,
} from '@wix/design-system';
import { horizontalNavBarDataHooks } from './data-hooks';
import styles from './horizontal-nav-bar.module.scss';

/** A nav bar item, in the shape the WDS Tabs renders - arrives render-ready
 *  from the producing service; this component only feeds it through. */
export interface HorizontalNavBarItem {
  id: string | number;
  title: React.ReactNode;
  type?: 'dropdown';
  items?: HorizontalNavBarSection[];
}

export interface HorizontalNavBarSection {
  title?: React.ReactNode;
  items: HorizontalNavBarSubItem[];
}

export interface HorizontalNavBarSubItem {
  id: string | number;
  title: React.ReactNode;
}

interface HorizontalNavBarBaseProps {
  items: HorizontalNavBarItem[];
  selectedItemKey?: string;
  onItemClick(item: HorizontalNavBarItem, index: number): void;
  onDropdownItemClick?(
    item: HorizontalNavBarSubItem,
    index: number,
    parentItem: HorizontalNavBarItem,
  ): void;
  onDropdownOpen?(item: HorizontalNavBarItem, index: number): void;
}

interface PillsNavBarProps extends HorizontalNavBarBaseProps {
  skin?: 'pills';
  sideContent?: React.ReactNode;
  topContent?: React.ReactNode;
}

interface TabsNavBarProps extends HorizontalNavBarBaseProps {
  skin: 'tabs';
  title: string;
  sideContent?: React.ReactNode;
}

export type HorizontalNavBarProps = PillsNavBarProps | TabsNavBarProps;

export function HorizontalNavBar(props: HorizontalNavBarProps) {
  if (props.skin === 'tabs') {
    return <TabsNavBar {...props} />;
  }
  return <PillsNavBar {...props} />;
}

function TabsNavBar({
  items,
  selectedItemKey,
  onItemClick,
  title,
  sideContent,
}: TabsNavBarProps) {
  return (
    <Box width="100%" gap="24px" verticalAlign="middle">
      <Heading size="extraLarge" dataHook={horizontalNavBarDataHooks.title}>
        {title}
      </Heading>
      <Tabs
        items={items}
        activeId={selectedItemKey}
        onClick={(item) => onItemClick(item, items.indexOf(item))}
        size="medium"
        showDivider={false}
        horizontalPadding={false}
        minWidth={0}
      />
      {sideContent ? (
        <Box
          dataHook={horizontalNavBarDataHooks.sideContent}
          flex="1 1 auto"
          align="right"
        >
          {sideContent}
        </Box>
      ) : null}
    </Box>
  );
}

function PillsNavBar({
  items,
  selectedItemKey,
  onItemClick,
  onDropdownItemClick,
  onDropdownOpen,
  sideContent,
  topContent,
}: PillsNavBarProps) {
  const [openItemId, setOpenItemId] = useState<
    HorizontalNavBarItem['id'] | undefined
  >();

  /* items overflow into horizontal scroll - keep the selected pill visible
     without re-centering on every route change (e.g. sub-category clicks) */
  useEffect(() => {
    const root = document.querySelector(
      `[data-hook="${horizontalNavBarDataHooks.root}"]`,
    );
    const selectedItem = root?.querySelector(
      '[role="tab"][aria-selected="true"]',
    );
    if (!(selectedItem instanceof HTMLElement)) {
      return;
    }

    const scrollContainer = root?.querySelector(
      '[data-hook="horizontal-scroll-scroll-container"]',
    );
    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const itemRect = selectedItem.getBoundingClientRect();
      const isFullyVisible =
        itemRect.left >= containerRect.left &&
        itemRect.right <= containerRect.right;
      if (isFullyVisible) {
        return;
      }
    }

    selectedItem.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selectedItemKey]);

  /* the pills strip scrolls sideways on overflow, and the dropdown is
     portalled to the window - scrolling would leave it floating away from
     its pill, so close it instead of letting it drift */
  useEffect(() => {
    if (openItemId === undefined) {
      return;
    }
    const close = () => setOpenItemId(undefined);
    document.addEventListener('scroll', close, true);
    return () => document.removeEventListener('scroll', close, true);
  }, [openItemId]);

  const renderedItems = items.map((item, index) => ({
    ...item,
    title:
      item.type === 'dropdown' && item.items?.length ? (
        <DropdownTabTitle
          item={item}
          shown={openItemId === item.id}
          onOpen={() => {
            if (openItemId === item.id) {
              return;
            }
            setOpenItemId(item.id);
            onDropdownOpen?.(item, index);
          }}
          onClose={() =>
            setOpenItemId((current) =>
              current === item.id ? undefined : current,
            )
          }
          onItemClick={(subItem, subItemIndex) => {
            onDropdownItemClick?.(subItem, subItemIndex, item);
            setOpenItemId(undefined);
          }}
        />
      ) : (
        item.title
      ),
  }));

  return (
    <Box direction="vertical" gap="12px" width="100%">
      {topContent ? (
        <Box dataHook={horizontalNavBarDataHooks.topContent} width="100%">
          {topContent}
        </Box>
      ) : null}
      <Tabs
        className={styles.pills}
        dataHook={horizontalNavBarDataHooks.root}
        items={renderedItems}
        activeId={selectedItemKey}
        /* dropdowns open on hover only - clicking a dropdown pill goes to its
           landing page, same as every other pill */
        onClick={(renderedItem) => {
          const index = renderedItems.findIndex(
            ({ id }) => id === renderedItem.id,
          );
          setOpenItemId(undefined);
          if (index !== -1) {
            onItemClick(items[index], index);
          }
        }}
        size="small"
        showDivider={false}
        horizontalPadding={false}
        scrollOnOverflow
        sideContent={
          sideContent ? (
            <Box dataHook={horizontalNavBarDataHooks.sideContent}>
              {sideContent}
            </Box>
          ) : undefined
        }
      />
    </Box>
  );
}

function DropdownTabTitle({
  item,
  shown,
  onOpen,
  onClose,
  onItemClick,
}: {
  item: HorizontalNavBarItem;
  shown: boolean;
  onOpen(): void;
  onClose(): void;
  onItemClick(item: HorizontalNavBarSubItem, index: number): void;
}) {
  let itemIndex = 0;

  return (
    <Popover
      appendTo="window"
      placement="bottom"
      shown={shown}
      showArrow
      moveBy={{ x: 0, y: 4 }}
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      onClickOutside={onClose}
    >
      <Popover.Element>
        <span
          className={styles.dropdownTrigger}
          data-hook={`${horizontalNavBarDataHooks.dropdownTrigger}-${item.id}`}
        >
          {item.title}
        </span>
      </Popover.Element>
      <Popover.Content>
        {/* the popover renders inside the pill's tab button, so without
            stopping propagation a click in here also clicks the pill */}
        <div
          className={styles.dropdownContent}
          data-hook={horizontalNavBarDataHooks.dropdownContent}
          onClick={(event) => event.stopPropagation()}
        >
          {item.items?.map((section, sectionIndex) => (
            <React.Fragment key={sectionIndex}>
              {sectionIndex > 0 ? (
                <div className={styles.dropdownDivider}>
                  <Divider />
                </div>
              ) : null}
              {section.title ? (
                <Text
                  className={styles.dropdownSectionTitle}
                  size="tiny"
                  secondary
                  weight="thin"
                >
                  {section.title}
                </Text>
              ) : null}
              {section.items.map((subItem) => {
                const index = itemIndex++;
                return (
                  <ListItemAction
                    as="button"
                    dataHook={`${horizontalNavBarDataHooks.dropdownItem}-${subItem.id}`}
                    key={subItem.id}
                    onClick={() => onItemClick(subItem, index)}
                    size="small"
                    title={subItem.title}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </Popover.Content>
    </Popover>
  );
}
